import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import LinkGroupCard from '../../components/Links/LinkGroupCard';
import MainMenuDrawer from '../../components/Main/MenuDrawer';
import Header from '../../components/common/Header';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../routes/paths';
import { getLinkGroupsApi, getLinkGroupItemsApi } from '../../api';

const LinkGroupPage = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleNavigate = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem('currentUser');
      if (raw) setCurrentUser(JSON.parse(raw));
      else setCurrentUser(null);
    } catch {
      setCurrentUser(null);
    }
  }, []);

  const isAdmin = currentUser?.role === 'ADMIN';

  const emptyState = useMemo(() => [], []);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      try {
        const [groupsRes, itemsRes] = await Promise.all([getLinkGroupsApi(), getLinkGroupItemsApi()]);
        const g = groupsRes?.data || [];
        const items = itemsRes?.data || [];

        const byGroup = {};
        for (const it of items) {
          const gid = it.groupId;
          if (!byGroup[gid]) byGroup[gid] = [];
          byGroup[gid].push(it);
        }

        const mapped = g.map((gr) => {
          const gid = gr.groupId;
          const rawItems = (byGroup[gid] || []).slice(0, 3);
          return {
            id: gid,
            title: gr.name,
            iconName: gr.iconName,
            items: rawItems.map((it, idx) => ({
              id: it.itemId,
              rank: Number(it.sortOrder) || idx + 1,
              label: it.title,
              url: it.url,
            })),
          };
        });

        if (alive) setGroups(mapped);
      } catch (e) {
        if (alive) setGroups(emptyState);
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [emptyState]);

  return (
    <Box>
      <Box
        sx={{
          maxWidth: 430,
          mx: 'auto',
          bgcolor: 'white',
          minHeight: '100vh',
          borderRadius: 0,
          boxShadow: '0 0 20px rgba(0,0,0,0.06)',
          px: 2,
          position: 'relative',
          pb: 2,
        }}
      >
        <MainMenuDrawer
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          onNavigate={handleNavigate}
        />

        <Header
          variant="full"
          onClickMenu={() => setMenuOpen(true)}
        />

        <Box sx={{ mb: 2.5, mt: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, fontSize: 24 }}>
            링크 모음
          </Typography>
          <Typography variant="body2" sx={{ color: '#999999', fontSize: 13 }}>
            자주 사용하는 사이트 바로가기
          </Typography>
        </Box>

        <Box
          sx={{
            width: '100%',
            maxWidth: 400,
            mx: 'auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 1.8,
          }}
        >
          {(groups || []).map((group) => (
            <Box key={group.id}>
              <LinkGroupCard group={group} />
            </Box>
          ))}
        </Box>

        {!loading && (!groups || groups.length === 0) && (
          <Box sx={{ mt: 3, textAlign: 'center', color: '#999', fontSize: 13 }}>
            등록된 링크모음이 없습니다.
          </Box>
        )}

        {isAdmin && (
          <Box
            onClick={() => navigate(PATHS.adminLinkGroups)}
            sx={{
              position: 'fixed',
              bottom: 50,
              right: 'max(24px, calc((100vw - 430px) / 2 + 24px))',
              minWidth: 72,
              height: 40,
              px: 2.5,
              py: 1,
              borderRadius: 999,
              bgcolor: '#3679FF',
              color: '#ffffff',
              fontSize: 14,
              fontWeight: 600,
              boxShadow: '0 6px 12px rgba(0,0,0,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 1200,
            }}
          >
            수정
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LinkGroupPage;


